import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { CustomerAuthProvider } from "@/contexts/CustomerAuthContext";
import { DataProvider } from "@/contexts/DataContext";
import LoginPage from "@/pages/LoginPage";
import ActivationPage from "@/pages/ActivationPage";
import OwnerSetup from "@/pages/OwnerSetup";
import SystemDashboard from "@/pages/SystemDashboard";
import EmployeeManager from "@/pages/EmployeeManager";
import MerchantDashboard from "@/pages/MerchantDashboard";
import IntegrationsPage from "@/pages/IntegrationsPage";
import LocationsManager from "@/pages/LocationsManager";
import AgencyDashboard from "@/pages/AgencyDashboard";
import AgencyOrganizationsPage from "@/pages/AgencyOrganizationsPage";
import AgencyEmployeesPage from "@/pages/AgencyEmployeesPage";
import AgencyBillingPage from "@/pages/AgencyBillingPage";
import RedeemerDashboard from "@/pages/RedeemerDashboard";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppLayout from "@/components/AppLayout";
import HeadIcons from "@/components/HeadIcons";
import PublicScanDemo from "@/pages/PublicScanDemo";
import ScanPage from "@/pages/ScanPage";
import VerifyPage from "@/pages/VerifyPage";
import ClaimPage from "@/pages/ClaimPage";
import PublicPage from "@/pages/PublicPage";
  import PagesAdmin from "@/pages/PagesAdmin";
  import PreviewPage from "@/pages/PreviewPage";
import ActionDesign from "@/pages/ActionDesign";
import { Toaster } from "@/components/ui/toaster";
import UpdatesPage from "@/pages/UpdatesPage";
import Footer from "@/components/Footer";
import ImpressumPage from "@/pages/ImpressumPage";
import DatenschutzPage from "@/pages/DatenschutzPage";
import StatusPage from "@/pages/StatusPage";
import SendmireinDashboard from "@/pages/SendmireinDashboard";
import SendmireinSettings from "@/pages/SendmireinSettings";
import SendmireinSubscribers from "@/pages/SendmireinSubscribers";
import SendmireinTemplateEditorVisual from "@/pages/SendmireinTemplateEditorVisual";
import SendmireinHistory from "@/pages/SendmireinHistory";
import SendmireinHistoryDetail from "@/pages/SendmireinHistoryDetail";
import { CookieConsentProvider } from "@/contexts/CookieConsentContext";
import CookieConsent from "@/components/CookieConsent";
import SessionsPage from "@/pages/SessionsPage";
import ResetPassword from "@/pages/ResetPassword";
import StaffLoginPage from "@/pages/StaffLoginPage";
import StaffDashboard from "@/pages/StaffDashboard";
import StaffSetPasswordPage from "@/pages/StaffSetPasswordPage";
import AgencySetPasswordPage from "@/pages/AgencySetPasswordPage";
import StaffSecuritySettings from "@/pages/StaffSecuritySettings";
import UserSecuritySettings from "@/pages/UserSecuritySettings";
import RootRedirect from "@/components/RootRedirect";
import NotificationsAdmin from "@/pages/NotificationsAdmin";
import NotificationsArchive from "@/pages/NotificationsArchive";
import LandingPage from "@/pages/LandingPage";
import CustomerLoginPage from "@/pages/CustomerLoginPage";
import CustomerDashboard from "@/pages/CustomerDashboard";
import CustomerProfile from "@/pages/CustomerProfile";
import CustomerResetPassword from "@/pages/CustomerResetPassword";
import CustomerVerifyEmail from "@/pages/CustomerVerifyEmail";
import StampCardsPage from "@/pages/StampCardsPage";
import { ThemeProvider } from "@/contexts/ThemeContext";

const App = () => (
  <ThemeProvider>
  <AuthProvider>
    <CustomerAuthProvider>
    <DataProvider>
      <CookieConsentProvider>
      <Router>
        <HeadIcons />
        <AppLayout>
          <CookieConsent />
          <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/activate" element={<ActivationPage />} />
          <Route path="/owner-setup" element={<OwnerSetup />} />
          <Route path="/agency/set-password" element={<AgencySetPasswordPage />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/staff/login" element={<Navigate to="/login" replace />} />
          <Route 
            path="/staff/dashboard" 
            element={
              <ProtectedRoute requiredRole="STAFF">
                <StaffDashboard />
              </ProtectedRoute>
            } 
          />
          <Route path="/staff/set-password" element={<StaffSetPasswordPage />} />
          <Route path="/staff/security" element={<StaffSecuritySettings />} />
          <Route
            path="/security"
            element={
              <ProtectedRoute requiredRole={["SYSTEM_OWNER","ORG_OWNER","AGENCY","REDEEMER"]}>
                <UserSecuritySettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/system"
            element={
              <ProtectedRoute requiredRole="SYSTEM_OWNER">
                <SystemDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/employees"
            element={
              <ProtectedRoute requiredRole="ORG_OWNER">
                <EmployeeManager />
              </ProtectedRoute>
            }
          />
          <Route
            path="/merchant"
            element={
              <ProtectedRoute requiredRole={["ORG_OWNER", "EMPLOYEE"]}>
                <MerchantDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/integrations"
            element={
              <ProtectedRoute requiredRole={["ORG_OWNER"]}>
                <IntegrationsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/stamp-cards"
            element={
              <ProtectedRoute requiredRole={["ORG_OWNER"]}>
                <StampCardsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/locations"
            element={
              <ProtectedRoute requiredRole={["ORG_OWNER"]}>
                <LocationsManager />
              </ProtectedRoute>
            }
          />
          <Route
            path="/agency"
            element={
              <ProtectedRoute requiredRole="AGENCY">
                <AgencyDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/agency/organizations"
            element={
              <ProtectedRoute requiredRole="AGENCY">
                <AgencyOrganizationsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/agency/employees"
            element={
              <ProtectedRoute requiredRole="AGENCY">
                <AgencyEmployeesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/agency/billing"
            element={
              <ProtectedRoute requiredRole="AGENCY">
                <AgencyBillingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/redeemer"
            element={
              <ProtectedRoute requiredRole={["REDEEMER", "EMPLOYEE", "ORG_OWNER"]}>
                <RedeemerDashboard />
              </ProtectedRoute>
            }
          />
          <Route path="/scan-demo" element={<PublicScanDemo />} />
          <Route path="/scan/:slug" element={<ScanPage />} />
          <Route path="/verify/:token" element={<VerifyPage />} />
          <Route path="/claim/:code" element={<ClaimPage />} />
          <Route path="/p/:slug" element={<PublicPage />} />
          <Route path="/updates" element={<UpdatesPage />} />
          <Route path="/impressum" element={<ImpressumPage />} />
          <Route path="/datenschutz" element={<DatenschutzPage />} />
          <Route path="/status" element={<StatusPage />} />
          <Route
            path="/pages-admin"
            element={
              <ProtectedRoute requiredRole={["ORG_OWNER", "SYSTEM_OWNER"]}>
                <PagesAdmin />
              </ProtectedRoute>
            }
          />
          <Route
            path="/preview/:id"
            element={
              <ProtectedRoute requiredRole={["ORG_OWNER", "SYSTEM_OWNER", "AGENCY"]}>
                <PreviewPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/actions/:id/design"
            element={
              <ProtectedRoute requiredRole={["ORG_OWNER", "SYSTEM_OWNER", "AGENCY"]}>
                <ActionDesign />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sendmirein"
            element={
              <ProtectedRoute requiredRole={["ORG_OWNER"]}>
                <SendmireinDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sendmirein/history"
            element={
              <ProtectedRoute requiredRole={["ORG_OWNER"]}>
                <SendmireinHistory />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sendmirein/history/:id"
            element={
              <ProtectedRoute requiredRole={["ORG_OWNER"]}>
                <SendmireinHistoryDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sendmirein/settings"
            element={
              <ProtectedRoute requiredRole={["ORG_OWNER"]}>
                <SendmireinSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sendmirein/settings"
            element={
              <ProtectedRoute requiredRole={["ORG_OWNER"]}>
                <SendmireinSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sendmirein/subscribers"
            element={
              <ProtectedRoute requiredRole={["ORG_OWNER"]}>
                <SendmireinSubscribers />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sendmirein/templates/:id"
            element={
              <ProtectedRoute requiredRole={["ORG_OWNER"]}>
                <SendmireinTemplateEditorVisual />
              </ProtectedRoute>
            }
          />
          <Route
            path="/notifications/archive"
            element={
              <ProtectedRoute requiredRole={["ORG_OWNER","EMPLOYEE","SYSTEM_OWNER","STAFF"]}>
                <NotificationsArchive />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/notifications"
            element={
              <ProtectedRoute requiredRole="SYSTEM_OWNER">
                <NotificationsAdmin />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sessions"
            element={
              <ProtectedRoute requiredRole={["SYSTEM_OWNER","ORG_OWNER","EMPLOYEE","AGENCY","REDEEMER"]}>
                <SessionsPage />
              </ProtectedRoute>
            }
          />
          
          {/* Customer Routes */}
          <Route path="/customer/login" element={<CustomerLoginPage />} />
          <Route path="/customer/reset-password" element={<CustomerResetPassword />} />
          <Route path="/customer/verify-email" element={<CustomerVerifyEmail />} />
          <Route path="/customer/dashboard" element={<CustomerDashboard />} />
          <Route path="/customer/profile" element={<CustomerProfile />} />
        </Routes>
        <Footer />
        </AppLayout>
        <Toaster />
      </Router>
      </CookieConsentProvider>
    </DataProvider>
  </CustomerAuthProvider>
  </AuthProvider>
  </ThemeProvider>
);

export default App;
